import { ChangeDetectionStrategy, Component, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { combineLatest, NEVER, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { NotificationLayout, NotificationTheme } from '@fcom/ui-components';
import { CmsArticle, CmsContent } from '@fcom/core-api/interfaces';
import { TimeService } from '@fcom/core/services';
import { LocalDate, stopPropagation } from '@fcom/core/utils';
import { FragmentService } from '@fcom/common/services';

import { BookingService } from '../../services';

const FINLAND_COUNTRY_CODE = 'FI';
export const COVID_COVER_ORIGIN_COUNTRY_CODES = [
  'FI',
  'SE',
  'DK',
  'NO',
  'EE',
  'DE',
  'AT',
  'CH',
  'FR',
  'NL',
  'BE',
  'IT',
  'ES',
];
export const COVID_COVER_VALID_BEFORE_PURCHASE_DATE = LocalDate.forDate(2021, 8, 30);
export const COVID_COVER_VALID_BEFORE_DEPARTURE_DATE = LocalDate.forDate(2021, 10, 31);

@Component({
  selector: 'fin-booking-notification',
  styleUrls: ['booking-notification.component.scss'],
  templateUrl: 'booking-notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingNotificationComponent implements OnInit {
  readonly NotificationLayout = NotificationLayout;

  notification$: Observable<{ article: CmsContent; type: string }>;

  modalOpen = false;

  /**
   * Whether to show as teaser or not
   */
  @Input()
  isFullWidth = true;

  /**
   * Theme to be shown
   */
  @Input()
  theme = NotificationTheme.INFO;

  constructor(
    private bookingService: BookingService,
    private timeService: TimeService,
    private fragmentService: FragmentService,
    @Inject(PLATFORM_ID) private platform: object
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platform)) {
      const isCovidCoverOrigin$ = this.bookingService.locations.pipe(
        map((flights) => COVID_COVER_ORIGIN_COUNTRY_CODES.includes(flights[0]?.origin?.countryCode?.toUpperCase()))
      );

      const isDomesticFinlandTrip$ = this.bookingService.locations.pipe(
        map((flights) =>
          flights.every((flight) => {
            return (
              flight?.origin?.countryCode?.toUpperCase() === FINLAND_COUNTRY_CODE &&
              flight?.destination?.countryCode?.toUpperCase() === FINLAND_COUNTRY_CODE
            );
          })
        )
      );

      const isDepartureBeforeCovidCoverExpiration$ = this.bookingService.locations.pipe(
        map(
          (flights) =>
            flights[0]?.departureDate &&
            flights[0].departureDate instanceof LocalDate &&
            flights[0].departureDate.lte(COVID_COVER_VALID_BEFORE_DEPARTURE_DATE)
        )
      );

      const isPurchasedBeforeCovidCoverStartDate$ = this.timeService.currentLocalDate.pipe(
        map((currentDate: LocalDate) => currentDate.lte(COVID_COVER_VALID_BEFORE_PURCHASE_DATE))
      );

      const shouldDisplayCovidCover$ = combineLatest([
        isCovidCoverOrigin$,
        isDomesticFinlandTrip$,
        isDepartureBeforeCovidCoverExpiration$,
        isPurchasedBeforeCovidCoverStartDate$,
      ]).pipe(
        map(
          ([
            isCovidCoverOrigin,
            isDomesticFinlandTrip,
            isDepartureBeforeCovidCoverExpiration,
            isPurchasedBeforeCovidCoverStartDate,
          ]) =>
            isCovidCoverOrigin &&
            !isDomesticFinlandTrip &&
            isDepartureBeforeCovidCoverExpiration &&
            isPurchasedBeforeCovidCoverStartDate
        )
      );

      this.notification$ = shouldDisplayCovidCover$.pipe(
        map((displayCovidCover) =>
          displayCovidCover ? 'fragments.covidCoverNotification.url' : 'fragments.bookingGlobalNotification.url'
        ),
        distinctUntilChanged(),
        switchMap((fragmentKey: string) =>
          this.fragmentService.getFragment<CmsArticle>(fragmentKey).pipe(
            map((article) => ({
              article,
              type: fragmentKey === 'fragments.covidCoverNotification.url' ? 'corona-cover' : 'flexible-booking',
            }))
          )
        )
      );
    } else {
      this.notification$ = NEVER;
    }
  }

  showModal(event: Event): void {
    this.modalOpen = !this.modalOpen;
    stopPropagation(event);
  }
}
